import Fuse from 'fuse.js'

// Manually curated external / help-page links surfaced through the local
// search feed. Each URL can be bound to many search terms — Fuse weights
// the `terms` array twice as heavily as the label so a query of "pricing"
// reliably maps to "Plickers Pro" even though "Pro" is not in the query.
//
// To add a new entry, append to EXTERNAL_LINKS — no other file needs to
// change. Selections route through window.open(url, '_blank', 'noopener,noreferrer').

export const EXTERNAL_LINKS = [
  {
    id: 'ext-pro',
    label: 'Plickers Pro',
    url: 'https://www.plickers.com/pro',
    icon: 'external-link',
    terms: ['pro', 'pricing', 'upgrade', 'plans', 'subscription', 'paid', 'premium'],
  },
  {
    id: 'ext-cards',
    label: 'Plickers Cards',
    url: 'https://help.plickers.com/hc/en-us/articles/360008948034',
    icon: 'external-link',
    terms: ['cards', 'paper cards', 'print cards', 'card sheets', 'printable'],
  },
  {
    id: 'ext-support',
    label: 'Support / Help Center',
    url: 'https://help.plickers.com/',
    icon: 'external-link',
    terms: ['support', 'help', 'contact', 'docs', 'documentation', 'faq', 'manual'],
  },
  {
    id: 'ext-privacy',
    label: 'Privacy Policy',
    url: 'https://www.plickers.com/privacy',
    icon: 'external-link',
    terms: ['privacy', 'policy', 'gdpr', 'data'],
  },
  {
    id: 'ext-terms',
    label: 'Terms of Service',
    url: 'https://www.plickers.com/terms',
    icon: 'external-link',
    terms: ['terms', 'tos', 'legal'],
  },
  {
    id: 'ext-blog',
    label: 'Plickers Blog',
    url: 'https://www.plickers.com/blog',
    icon: 'external-link',
    terms: ['blog', 'news', 'updates', 'articles'],
  },
  {
    id: 'ext-ios-app',
    label: 'Get iOS App',
    url: 'https://itunes.apple.com/us/app/plickers/id701184049?mt=8',
    icon: 'external-link',
    terms: ['ios', 'iphone', 'ipad', 'apple', 'app store', 'download', 'mobile', 'app'],
  },
  {
    id: 'ext-android-app',
    label: 'Get Android App',
    url: 'https://play.google.com/store/apps/details?id=com.plickers.client.android',
    icon: 'external-link',
    terms: ['android', 'google play', 'download', 'mobile', 'app'],
  },
]

// Exported so the internal search-tuning sandbox can display/reset the defaults.
export const EXTERNAL_LINKS_FUSE_OPTIONS = {
  threshold: 0.2,
  ignoreLocation: true,
  minMatchCharLength: 1,
  includeScore: true,
  includeMatches: true,
  keys: [
    { name: 'terms', weight: 2 },
    { name: 'label', weight: 1 },
  ],
}

let cachedFuse = null

/**
 * Build (and cache) the Fuse external-links index. The registry is static
 * for the lifetime of the page so we only build once. The cache is keyed on
 * EXTERNAL_LINKS.length so adding entries automatically invalidates it.
 */
export function buildExternalLinksFuseIndex() {
  if (cachedFuse && cachedFuse.__entryCount === EXTERNAL_LINKS.length) return cachedFuse
  cachedFuse = new Fuse(EXTERNAL_LINKS, EXTERNAL_LINKS_FUSE_OPTIONS)
  cachedFuse.__entryCount = EXTERNAL_LINKS.length
  return cachedFuse
}

/**
 * Run a query against the external-links Fuse index and return Algolia-shaped
 * flat hits, each carrying __external: true, __localOrigin: true, plus the
 * registry fields (label, url, icon, terms). The Fuse score and per-key
 * match metadata are passed through as __localScore and __localMatches for
 * devtools / searchReporter inspection.
 */
export function searchExternalLinks(fuse, query, limit = 6) {
  if (!query || !fuse) return []
  return fuse.search(query, { limit }).map(({ item, score, matches }) => ({
    ...item,
    objectID: item.id,
    __external: true,
    __localOrigin: true,
    __localScore: score,
    __localMatches: matches,
  }))
}

// Single source of truth for the help-center deep link. Used by the dropdown's
// "See all results for help" row to push the user's query into the Plickers
// Help Center search.
const HELP_SEARCH_URL_BASE = 'https://help.plickers.com/hc/en-us/search'

export function buildHelpSearchUrl(query) {
  const trimmed = (query || '').trim()
  if (!trimmed) return HELP_SEARCH_URL_BASE
  return `${HELP_SEARCH_URL_BASE}?utf8=%E2%9C%93&query=${encodeURIComponent(trimmed)}`
}
